import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { 
  FileText, 
  MagnifyingGlass, 
  Printer,
  Download,
  Receipt,
  CurrencyDollar,
  ArrowClockwise,
  User,
  Calendar,
  CheckCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function PatientStatementPage() {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // For patient role, auto-load their own statement
  useEffect(() => {
    if (user?.role === 'patient') {
      loadStatement(user.id);
    } else {
      fetchPatients();
    }
  }, [user]);

  const fetchPatients = async () => {
    try {
      const response = await api.get('/admin/users?role=patient');
      const patientList = Array.isArray(response.data) ? response.data : (response.data.users || []);
      setPatients(patientList);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching patients:', error);
    }
  };

  const loadStatement = async (patientId) => {
    setLoading(true);
    try {
      const response = await api.get(`/billing/statement/${patientId}`);
      setStatement(response.data);
      
      // Find patient info if not set
      if (!selectedPatient && response.data.patient) {
        setSelectedPatient({
          id: patientId,
          first_name: response.data.patient.name.split(' ')[0],
          last_name: response.data.patient.name.split(' ').slice(1).join(' '),
          email: response.data.patient.email
        });
      }
    } catch (error) {
      toast.error('Failed to load statement');
    } finally {
      setLoading(false);
    }
  };

  const printStatement = () => {
    if (!selectedPatient) return;
    
    // Open the HTML statement in a new window for printing
    const printWindow = window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/billing/statement/${selectedPatient.id}/html`,
      '_blank'
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const filteredPatients = patients.filter(p => 
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-700',
      pending: 'bg-yellow-100 text-yellow-700',
      submitted: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      partial: 'bg-amber-100 text-amber-700',
      denied: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-8" data-testid="patient-statement-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Patient Statement
        </h1>
        <p className="text-slate-500">
          {user?.role === 'patient' ? 'View your billing statement' : 'Generate patient billing statements'}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Patient Selection (Staff only) */}
        {user?.role !== 'patient' && (
          <div className="lg:col-span-1">
            <div className="card-clinical p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Select Patient</h2>
              
              <div className="relative mb-4">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search patients..."
                  className="input-clinical pl-10"
                />
              </div>
              
              <div className="max-h-96 overflow-y-auto space-y-2">
                {filteredPatients.slice(0, 20).map((patient) => (
                  <button
                    key={patient.id}
                    onClick={() => {
                      setSelectedPatient(patient);
                      loadStatement(patient.id);
                    }}
                    className={`w-full p-3 rounded-lg text-left transition-colors ${
                      selectedPatient?.id === patient.id
                        ? 'bg-teal-50 border border-teal-200'
                        : 'bg-slate-50 hover:bg-slate-100 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                        <User size={20} className="text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{patient.first_name} {patient.last_name}</p>
                        <p className="text-sm text-slate-500">{patient.email}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Statement Content */}
        <div className={user?.role === 'patient' ? 'lg:col-span-3' : 'lg:col-span-2'}>
          {loading ? (
            <div className="card-clinical p-12 text-center">
              <ArrowClockwise size={32} className="animate-spin mx-auto mb-4 text-teal-600" />
              <p className="text-slate-500">Loading statement...</p>
            </div>
          ) : !statement ? (
            <div className="card-clinical p-12 text-center">
              <FileText size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">
                {user?.role === 'patient' 
                  ? 'No billing information available'
                  : 'Select a patient to view their statement'}
              </p>
            </div>
          ) : (
            <>
              {/* Account Summary */}
              <div className="card-clinical p-6 mb-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">{statement.patient?.name}</h2>
                    <p className="text-slate-500">{statement.patient?.email}</p>
                  </div>
                  <button
                    onClick={printStatement}
                    className="btn-teal flex items-center gap-2"
                  >
                    <Printer size={18} />
                    Print Statement
                  </button>
                </div>
                
                {/* Summary Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500 mb-1">Total Charges</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatCurrency(statement.account_summary?.total_charges)}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-blue-600 mb-1">Insurance Paid</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {formatCurrency(statement.account_summary?.insurance_payments)}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-green-600 mb-1">You Paid</p>
                    <p className="text-2xl font-bold text-green-700">
                      {formatCurrency(statement.account_summary?.patient_payments)}
                    </p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4">
                    <p className="text-sm text-amber-600 mb-1">Balance Due</p>
                    <p className="text-2xl font-bold text-amber-700">
                      {formatCurrency(statement.account_summary?.balance_due)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Invoice Details */}
              <div className="card-clinical p-6 mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Invoice Details</h3>
                
                {statement.invoices?.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No invoices found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Date</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Description</th>
                          <th className="text-right py-3 px-2 text-sm font-medium text-slate-600">Charges</th>
                          <th className="text-right py-3 px-2 text-sm font-medium text-slate-600">Insurance</th>
                          <th className="text-right py-3 px-2 text-sm font-medium text-slate-600">Paid</th>
                          <th className="text-right py-3 px-2 text-sm font-medium text-slate-600">Balance</th>
                          <th className="text-center py-3 px-2 text-sm font-medium text-slate-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statement.invoices?.map((inv) => (
                          <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-2 text-sm">{formatDate(inv.date)}</td>
                            <td className="py-3 px-2">
                              <p className="text-sm text-slate-900">{inv.description}</p>
                              {inv.items?.length > 0 && (
                                <p className="text-xs text-slate-500 mt-1">
                                  {inv.items.map(i => i.code).join(', ')}
                                </p>
                              )}
                            </td>
                            <td className="py-3 px-2 text-right text-sm">{formatCurrency(inv.charges)}</td>
                            <td className="py-3 px-2 text-right text-sm text-blue-600">
                              -{formatCurrency(inv.insurance_paid)}
                            </td>
                            <td className="py-3 px-2 text-right text-sm text-green-600">
                              -{formatCurrency(inv.patient_paid)}
                            </td>
                            <td className="py-3 px-2 text-right text-sm font-medium">
                              {formatCurrency(inv.balance)}
                            </td>
                            <td className="py-3 px-2 text-center">
                              {getStatusBadge(inv.status)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-50">
                          <td colSpan="5" className="py-3 px-2 text-right font-medium">
                            Total Balance Due:
                          </td>
                          <td className="py-3 px-2 text-right font-bold text-lg text-teal-600">
                            {formatCurrency(statement.account_summary?.balance_due)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>

              {/* Payment History */}
              {statement.payments?.length > 0 && (
                <div className="card-clinical p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Payment History</h3>
                  
                  <div className="space-y-3">
                    {statement.payments.map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <CheckCircle size={24} className="text-green-500" />
                          <div>
                            <p className="font-medium text-slate-900">{formatCurrency(payment.amount)}</p>
                            <p className="text-sm text-slate-500">
                              {payment.method} - {formatDate(payment.date)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment Notice */}
              {statement.account_summary?.balance_due > 0 && (
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <CurrencyDollar size={24} className="text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Payment Due</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Your balance of {formatCurrency(statement.account_summary?.balance_due)} is due within 30 days. 
                        You can pay online through the portal, by phone, or at your next visit.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
